import { useQuery } from "@tanstack/react-query"
import { locationService } from "../../services/locationService"
import { useContext, useEffect, useState, useCallback } from "react"
import UiContext from "../../context/uiProvider"
import { DESKTOP, TABLET } from "../../utils/screenSize"
import useUser from "../../hooks/useUser"
import { Location } from "../../Interfaces"
import Table from "./common/Table"
import { useSiteTerminology } from "../../contexts/PublicSiteContext"
import { useSelectedCompany } from "../../context/companyProvider"

interface LocationsTableProps {
  userProfileId?: number;
}

export default function LocationsTable({ userProfileId = undefined }: LocationsTableProps) {
  const { id } = useUser()
  const { deviceView } = useContext(UiContext)
  const { locationsLabel } = useSiteTerminology()
  const { selectedCompany } = useSelectedCompany()
  const [searchTerm, setSearchTerm] = useState<string>("")
  const userId = userProfileId || id
  const companyId = selectedCompany?.id

  const { data, isLoading } = useQuery<Partial<Location[]>>(
    ["locations", "userId" + userId, companyId],
    () => locationService.getAllByUser(userId, companyId),
    { cacheTime: 5000, suspense: true },
  )

  const [filteredData, setFilteredData] = useState<Location[] | undefined | null>(null)

  useEffect(() => {
    if (data) {
      // Filter out undefined values and then apply search filter
      const validLocations = data.filter((location): location is Location => location !== undefined)
        .filter((location) => !companyId || location.managementCompanyId == null || location.managementCompanyId === companyId)

      if (searchTerm) {
        const filtered = validLocations.filter(location =>
          location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.zipcode?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
        setFilteredData(filtered)
      } else {
        setFilteredData(validLocations)
      }
    }
  }, [data, searchTerm, companyId])

  const headers: { label: string, key: string, to?: string }[] = [
    { label: "Id", key: "id", to: `/app/locations` },
    { label: "Name", key: "name", to: `/app/locations` },
  ]

  // Add two more columns on tablets and desktops
  if (deviceView >= TABLET) {
    headers.push({ label: "Address", key: "address" }, { label: "City", key: "city" }, { label: "State", key: "state" }, { label: "Zipcode", key: "zipcode" })
  }
  // Add two additional columns on desktops
  if (deviceView >= DESKTOP) {
    // headers.push({ label: "Latitude", key: "latitude" }, { label: "Longitude", key: "longitude" })
  }

  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query)
  }, [])

  if (!filteredData || isLoading) return null

  return (
    <Table data={filteredData} title={locationsLabel} to={"/app/addLocation"} handleSearch={handleSearch} headers={headers} />
  )
}
